import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Calculator, Phone, Mail, CheckCircle2, FileText, Users, Shield, Laptop } from 'lucide-react';
import { toast } from 'sonner';

const TaxDemo = () => {
  const { t } = useTranslation();

  const handleConsult = () => {
    toast.success(`✅ ${t('toast.bookingSuccess')}`, {
      description: t('toast.bookingSuccessDesc')
    });
  };

  const services = [
    { icon: FileText, name: t('taxDemo.service1'), desc: t('taxDemo.service1Desc') },
    { icon: Users, name: t('taxDemo.service2'), desc: t('taxDemo.service2Desc') },
    { icon: Calculator, name: t('taxDemo.service3'), desc: t('taxDemo.service3Desc') },
    { icon: FileText, name: t('taxDemo.service4'), desc: t('taxDemo.service4Desc') },
  ];

  const features = [
    { name: t('taxDemo.feature1'), icon: Shield },
    { name: t('taxDemo.feature2'), icon: Users },
    { name: t('taxDemo.feature3'), icon: Laptop },
    { name: t('taxDemo.feature4'), icon: CheckCircle2 },
  ];

  const whyUs = [
    { title: t('taxDemo.whyUs1'), icon: CheckCircle2 },
    { title: t('taxDemo.whyUs2'), icon: Users },
    { title: t('taxDemo.whyUs3'), icon: Laptop },
    { title: t('taxDemo.whyUs4'), icon: Phone },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white/90 hover:bg-white shadow-lg">
            <ArrowLeft size={16} className="mr-2" />
            {t('common.backToPortfolio')}
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
              <Calculator className="text-white" size={20} />
            </div>
            <div>
              <span className="font-semibold text-gray-900">{t('taxDemo.name')}</span>
              <p className="text-xs text-gray-500">{t('taxDemo.tagline')}</p>
            </div>
          </div>
          <Button onClick={handleConsult} className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white">
            <Phone size={16} className="mr-2" />
            {t('taxDemo.consultButton')}
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80')] bg-cover bg-center opacity-5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
              <Shield size={16} />
              {t('taxDemo.tagline')}
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('taxDemo.heroTitle')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('taxDemo.heroDesc')}
            </p>
            <Button onClick={handleConsult} size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white">
              {t('taxDemo.consultButton')}
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Unsere Leistungen</h2>
            <p className="text-gray-600">Umfassende Steuerberatung für Privatpersonen und Unternehmen</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <Card key={i} className="border-2 border-slate-200 hover:border-blue-300 transition-all hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mx-auto mb-4">
                    <service.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-sm text-gray-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Unsere Vorteile</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                  <feature.icon className="text-blue-600" size={24} />
                </div>
                <p className="font-semibold text-gray-900">{feature.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('taxDemo.whyUsTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((item, i) => (
              <Card key={i} className="border-2 border-slate-200">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="text-blue-600" size={24} />
                  </div>
                  <p className="font-semibold text-gray-900">{item.title}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Kundenstimmen</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle2 key={i} className="text-green-500 fill-green-500" size={18} />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">{t('taxDemo.testimonial1')}</p>
                <p className="font-semibold text-gray-900">Thomas M.</p>
                <p className="text-sm text-gray-500">Geschäftsführer</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle2 key={i} className="text-green-500 fill-green-500" size={18} />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">{t('taxDemo.testimonial2')}</p>
                <p className="font-semibold text-gray-900">Andrea S.</p>
                <p className="text-sm text-gray-500">Freiberuflerin</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">{t('taxDemo.ctaTitle')}</h2>
          <p className="text-xl text-blue-100 mb-8">{t('taxDemo.ctaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleConsult} size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              <Phone size={20} className="mr-2" />
              +49 123 456789
            </Button>
            <Button onClick={handleConsult} size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10">
              <Mail size={20} className="mr-2" />
              info@steuerexperten.de
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Note */}
      <div className="fixed bottom-4 right-4 bg-white border-2 border-gray-200 rounded-lg p-3 shadow-lg text-sm text-gray-600">
        <p className="font-semibold">{t('common.demoProject')}</p>
      </div>
    </div>
  );
};

export default TaxDemo;